import { motion } from 'framer-motion';
import { Truck, MapPin, Clock, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function Delivery() {
  const { t } = useLanguage();
  
  const deliveryZones = [
    {
      icon: MapPin,
      title: t('deliveryLiege'),
      titleEn: 'Delivery in Liège',
      price: '5,50€',
      description: 'Liège centre et environs immédiats',
      descriptionEn: 'Liège center and immediate surroundings',
      color: 'bg-secondary',
    },
    {
      icon: Truck,
      title: t('deliveryPeriphery'),
      titleEn: 'Liège Periphery',
      price: '7,50€',
      description: 'Seraing, Herstal, Ans, Flémalle, Chaudfontaine, Grace-Hollogne',
      descriptionEn: 'Seraing, Herstal, Ans, Flémalle, Chaudfontaine, Grace-Hollogne',
      color: 'bg-accent/30',
    },
    {
      icon: Package,
      title: t('deliveryOutside'),
      titleEn: 'Outside Liège',
      price: '14,50€',
      description: 'Minimum de commande: 75€',
      descriptionEn: 'Minimum order: 75€',
      color: 'bg-primary/10',
    },
  ];
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="delivery-page">
      {/* Header */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-secondary/30 to-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Livraison</span>
            <h1 className="text-3xl lg:text-5xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="delivery-title">
              {t('deliveryPolicy')}
            </h1>
            <p className="text-lg text-text_secondary mt-4">
              {t('deliveryTitle')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Delivery Image Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden shadow-2xl"
          >
            <img
              src="https://images.pexels.com/photos/6169662/pexels-photo-6169662.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Livraison AfriOne B&D"
              className="w-full h-64 lg:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
              <div className="p-6 lg:p-8">
                <h3 className="text-white text-2xl lg:text-3xl font-bold mb-2">
                  Livraison Rapide & Sécurisée
                </h3>
                <p className="text-white/90 text-sm lg:text-base">
                  Vos produits africains livrés avec soin à votre porte
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Delivery Zones */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {deliveryZones.map((zone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-8 rounded-3xl ${zone.color}`}
                data-testid={`delivery-zone-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-6 shadow-sm">
                  <zone.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold font-heading text-text_primary mb-2">
                  {zone.title}
                </h3>
                <p className="text-3xl font-bold text-primary mb-4">
                  {zone.price}
                </p>
                <p className="text-text_secondary">
                  {zone.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Additional Info */}
      <section className="py-16 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold font-heading text-text_primary tracking-tight mb-6">
                Informations importantes
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_primary">Délai de livraison</h4>
                    <p className="text-text_secondary">Livraison sous 24 à 48h après confirmation de la commande</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Package className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_primary">Emballage surgelé</h4>
                    <p className="text-text_secondary">Tous nos produits surgelés sont livrés dans des emballages isothermiques</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Truck className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_primary">Suivi de livraison</h4>
                    <p className="text-text_secondary">Vous recevrez un SMS ou appel avant la livraison</p>
                  </div>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600"
                alt="Delivery"
                className="w-full rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-lg">
                <p className="text-3xl font-bold text-primary">24-48h</p>
                <p className="text-sm text-text_secondary">Délai moyen</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
